import { Alert, AlertModal, DisplayValue } from '..'
import Ajax from '../Ajax'
import { store } from './Store'
import { Report } from '../models/Report'
import { makeAutoObservable } from 'mobx'
import ReportRequestForm from '../components/form/report/ReportRequestForm'
import { UTCDate } from '@date-fns/utc'


export class ReportStore {

    reports: Report[] = [];
    searchString: string = "";
    orderedBy: string = "requestedAt";
    order: string = "desc";
    loading: boolean = true;

    constructor () {

        makeAutoObservable(this)
    }

    get enquiryReports () {
        var reports = []

        if (store.EnquiryStore.enquiry) {
            var rSection = store.EnquiryStore.enquiry?.childrenObjects?.find(section => section.name === "REPORTS")
            if (rSection) {
                reports = rSection.childrenObjects
            }
        }

        // Order by date
        // reports = [...reports].sort((a, b) => {
        //     return new UTCDate(a.generatedAt).getTime() - new UTCDate(b.generatedAt).getTime()
        // })

        // Order by id and version
        reports = [...reports].sort((a, b) => a.uid?.localeCompare(b.uid))

        return reports
    }

    get enquiryReportsCurrentOnly () {

        // var reports = [...this.enquiryReports?.filter(r => r.valid || r.fileStatus === "R")]
        var reports = [...this.enquiryReports]

        reports = reports.filter(report => {
            var otherReports = reports.filter(r => r.statusId === report.statusId && r.reportType === report.reportType && r.uid !== report.uid)
            var newerAlternative = otherReports.find(r => (!report.valid || (report.valid && r.valid)) && new UTCDate(report.requestedAt) < new UTCDate(r.requestedAt)) // Old reports can sometime be revalidated.
            var validAlternative = otherReports.find(r => !report.valid && r.valid) // If 'report' is not valid
            
            if (report.fileStatus === "R") return true
            if (newerAlternative) return false
            if (validAlternative) return false
            return true
        })

        return reports
    }

    checkForPendingReports = () => {
        var pendingReport = this.enquiryReports?.find(r => r.fileStatus === "R")

        // Reload if report is in process of regenerating
        if (pendingReport) {
            return setTimeout(() => this.refreshReports(), 5000)
        }
    }


    openReportRequestForm = async () => {
        return await Ajax.Enquiry.GetReportOptions(store.EnquiryStore.enquiry.uid).then((response) => {
            AlertModal({id: "report_options", body: <ReportRequestForm enquiry={store.EnquiryStore.enquiry} reportOptions={response.data} />, width: "600px"})
        }).catch((response) => {
            Alert({message: response?.data?.detail ? response.data.detail : "Couldn't get report options"})
        })
    }

    // #region Filtering

    setOrder = (order) => {
        this.order = order
    }

    setOrderedBy = (orderedBy) => {
        this.orderedBy = orderedBy
    }

    setSearchString = (str) => {
        this.searchString = str
    }

    get orderPhrase () {
        var orderPhrase = this.order === "asc" ? ", reverse" : ""

        if (["On", "At"].includes(this.orderedBy.slice(-2))) {
            orderPhrase = this.order === "asc" ? ", oldest first" : ", newest first"
        }

        return `ordered by '${DisplayValue("dict_key", this.orderedBy)}'${orderPhrase}`
    }

    filterReports = (reports: Report[]) => {

        if (!Array.isArray(reports) || !reports.length){
            return []
        }

        // Filtering
        if (this.searchString) {
            reports = reports.filter(r => {
                return (
                    r.case?.name?.toLowerCase().includes(this.searchString.toLowerCase()) ||
                    r.subject?.name?.toLowerCase().includes(this.searchString.toLowerCase()) ||
                    r.requestedBy?.name?.toLowerCase().includes(this.searchString.toLowerCase())
                )
            })
        }

        // Ordering
        reports = [...reports].sort((a, b) => {

            var aValue = null
            var bValue = null

            if (typeof a[this.orderedBy] === "object") {
                aValue = a[this.orderedBy].name
                bValue = b[this.orderedBy].name
            } else if (Object.keys(a).includes(this.orderedBy) && Object.keys(b).includes(this.orderedBy)) {
                aValue = a[this.orderedBy]
                bValue = b[this.orderedBy]
            }

            // Date fields need opposite ordering
            if (typeof a[this.orderedBy] === "number" || ["On", "At"].includes(this.orderedBy.slice(-2))) {
                return this.order === "desc" ? bValue?.localeCompare(aValue) : aValue?.localeCompare(bValue)
            }
            
            return this.order === "desc" ? aValue?.localeCompare(bValue) : bValue?.localeCompare(aValue)
        })

        return reports
    }

    // #endregion

    groupByNationality = (reports) => {

        var reportsByNationality = {}

        reports.forEach(report => {
            if (report.statusId in reportsByNationality) {
                reportsByNationality[report.statusId].push(report)
            } else {
                reportsByNationality[report.statusId] = [report]
            }
        })

        return reportsByNationality
    }

    refreshReports = async () => {
        if (store.EnquiryStore.enquiry) {
            await Ajax.Node.Get(store.EnquiryStore.enquiry.uid).then((response) => {
                store.NodeStore.setLeafNode(response.data)
            })
        }
        if (this.reports.length) {
            await Ajax.Report.All().then((response) => {
                this.reports = response.data
            })
        }

    }

    getReports = async () => {

        this.loading = true
        this.setReports([])

        await Ajax.Report.All().then((response) => {
            this.reports = response.data
        }).finally(() => {
            this.loading = false
        })
    }

    setReports = (reports: any[]) => {
        this.reports = reports
    }

    makeInvalid = async (reportNode) => {

        await Ajax.Report.MarkInvalid(reportNode).catch(response => {
            Alert({message: response?.data?.detail})
        })

        this.refreshReports()
    }

    regenerate = async (reportNode) => {

        await Ajax.Report.Regenerate(reportNode).then(() => {
            store.NodeStore.refreshLeafNode()
        }).catch(response => {
            Alert({message: response?.data?.detail})
        })

        this.refreshReports()
    }

    download = async (reportNode) => {
        await Ajax.Report.File(reportNode, `${reportNode.label}.docx`).catch((response) => {
            Alert({message: "Couldn't download report"})
        })
    }

    orderAllReports = async (enquiryUid: string) => {
        await Ajax.Enquiry.GetReportOptions(enquiryUid).then(async (response) => {

            var reportOptionsToSend = response.data?.filter(ro => ro.messageId !== "report_provided").map(ro => {
                return {
                    enquiryUid: enquiryUid,
                    userReference: null,
                    statusId: ro.statusId,
                    reportType: ro.reportType,
                    ndsAccountUid: ro.ndsAccountUid,
                    customerType: ro.customerType,
                    accountUid: ro.accountUid,
                }
            })

            if (reportOptionsToSend?.length) {
                await Ajax.Report.Request(reportOptionsToSend).then(() =>
                    store.NodeStore.refreshLeafNode()
                )
            }

        }).catch((response) => {
            Alert({message: response?.data?.detail ? response.data.detail : "Couldn't get report options"})
        })
    }
}